import React, { useState, useEffect } from 'react';
import { ref, onValue, update, remove } from 'firebase/database';
import { db } from '../firebase';
import { Application, ContactMessage, Course, Announcement, MessageThread } from '../types';
import CoursesTab from '../components/admin/CoursesTab';
import ExamsTab from '../components/admin/ExamsTab';
import AnnouncementsTab from '../components/admin/AnnouncementsTab';
import StudentsTab from '../components/admin/StudentsTab';
import AdminMessagesTab from '../components/admin/AdminMessagesTab';

const ADMIN_PASSWORD = 'uaa2025admin';

type Tab = 'dashboard' | 'applications' | 'contacts' | 'courses' | 'exams' | 'announcements' | 'students' | 'studentMessages';

const statusLabels: Record<string, string> = {
  new: 'New', reviewing: 'Reviewing', accepted: 'Accepted', rejected: 'Rejected',
};

const degreeLabels: Record<string, string> = {
  bachelor: "Bachelor's", master: "Master's", doctorate: 'Doctoral',
};

const fmt = (ts: number) =>
  new Date(ts).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const SealSVG = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6L21 9 12 3zm5 10.99l-5 2.73-5-2.73V11.7L12 14.43l5-2.73v1.29z"/>
  </svg>
);

// ── Lock Screen ──────────────────────────────────────────────────────────────
const LockScreen: React.FC<{ onUnlock: () => void }> = ({ onUnlock }) => {
  const [pass, setPass] = useState('');
  const [err, setErr] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pass === ADMIN_PASSWORD) {
      sessionStorage.setItem('uaa_admin', '1');
      onUnlock();
    } else {
      setErr(true);
      setPass('');
    }
  };

  return (
    <div className="admin-lock">
      <div className="admin-lock-card">
        <div className="admin-lock-logo"><SealSVG /></div>
        <h2>Administration Panel</h2>
        <p>University of American Atlantic</p>
        <form onSubmit={handleSubmit}>
          <label className="admin-lock-label">Access Password</label>
          <input
            type="password"
            value={pass}
            onChange={e => { setPass(e.target.value); setErr(false); }}
            placeholder="Enter password"
            autoFocus
          />
          <button type="submit">Access Panel</button>
          {err && <p className="err">Incorrect password. Access denied.</p>}
        </form>
      </div>
    </div>
  );
};

// ── Application Modal ─────────────────────────────────────────────────────────
const AppModal: React.FC<{ app: Application; onClose: () => void }> = ({ app, onClose }) => {
  const handleStatus = (status: Application['status']) =>
    update(ref(db, `applications/${app.id}`), { status });

  const handleDelete = async () => {
    if (window.confirm('Permanently delete this application record?')) {
      await remove(ref(db, `applications/${app.id}`));
      onClose();
    }
  };

  return (
    <div className="a-modal-overlay" onClick={onClose}>
      <div className="a-modal" onClick={e => e.stopPropagation()}>
        <div className="a-modal-header">
          <h3>Application Record</h3>
          <button className="a-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="a-modal-row">
          <div className="a-modal-field"><label>Full Name</label><p>{app.fullName}</p></div>
          <div className="a-modal-field"><label>Email</label><p>{app.email}</p></div>
        </div>
        <div className="a-modal-row">
          <div className="a-modal-field"><label>Phone</label><p>{app.phone || '—'}</p></div>
          <div className="a-modal-field"><label>Country</label><p>{app.country}</p></div>
        </div>
        <div className="a-modal-row">
          <div className="a-modal-field"><label>Program</label><p>{app.program}</p></div>
          <div className="a-modal-field"><label>Degree Level</label><p>{degreeLabels[app.degree] || app.degree}</p></div>
        </div>
        <div className="a-modal-field" style={{ marginBottom: 18 }}>
          <label>Applicant Message</label>
          <div className="a-modal-msg">{app.message || '—'}</div>
        </div>
        <div className="a-modal-row">
          <div className="a-modal-field">
            <label>Application Status</label>
            <select
              className="a-status-select"
              value={app.status}
              onChange={e => handleStatus(e.target.value as Application['status'])}
            >
              <option value="new">New</option>
              <option value="reviewing">Reviewing</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="a-modal-field"><label>Submission Date</label><p>{fmt(app.createdAt)}</p></div>
        </div>
        <div className="a-modal-actions">
          <span />
          <button className="a-btn a-btn-delete" onClick={handleDelete}>Delete Record</button>
        </div>
      </div>
    </div>
  );
};

// ── Contact Modal ─────────────────────────────────────────────────────────────
const ContactModal: React.FC<{ msg: ContactMessage; onClose: () => void }> = ({ msg, onClose }) => {
  useEffect(() => {
    if (!msg.read) update(ref(db, `contacts/${msg.id}`), { read: true });
  }, [msg.id, msg.read]);

  const handleDelete = async () => {
    if (window.confirm('Permanently delete this message?')) {
      await remove(ref(db, `contacts/${msg.id}`));
      onClose();
    }
  };

  return (
    <div className="a-modal-overlay" onClick={onClose}>
      <div className="a-modal" onClick={e => e.stopPropagation()}>
        <div className="a-modal-header">
          <h3>Contact Message</h3>
          <button className="a-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="a-modal-row">
          <div className="a-modal-field"><label>From</label><p>{msg.fullName}</p></div>
          <div className="a-modal-field"><label>Email</label><p>{msg.email}</p></div>
        </div>
        <div className="a-modal-row">
          <div className="a-modal-field"><label>Phone</label><p>{msg.phone || '—'}</p></div>
          <div className="a-modal-field"><label>Received</label><p>{fmt(msg.createdAt)}</p></div>
        </div>
        <div className="a-modal-field" style={{ marginBottom: 16 }}>
          <label>Subject</label>
          <p style={{ color: '#fff', fontWeight: 600, marginTop: 5, fontFamily: "'Segoe UI', system-ui, sans-serif" }}>{msg.subject}</p>
        </div>
        <div className="a-modal-field">
          <label>Message</label>
          <div className="a-modal-msg">{msg.message}</div>
        </div>
        <div className="a-modal-actions">
          <a href={`mailto:${msg.email}?subject=Re: ${msg.subject}`} className="a-btn a-btn-reply">
            Reply via Email
          </a>
          <button className="a-btn a-btn-delete" onClick={handleDelete}>Delete Message</button>
        </div>
      </div>
    </div>
  );
};

// ── Dashboard Tab ─────────────────────────────────────────────────────────────
const DashboardTab: React.FC<{
  apps: Application[];
  contacts: ContactMessage[];
  courses: Course[];
  announcements: Announcement[];
  unreadThreads: number;
}> = ({ apps, contacts, courses, announcements, unreadThreads }) => {
  const newApps = apps.filter(a => a.status === 'new').length;
  const accepted = apps.filter(a => a.status === 'accepted').length;
  const unread = contacts.filter(c => !c.read).length;

  return (
    <>
      <h2 className="admin-page-title">Dashboard</h2>
      <p className="admin-page-sub">Overview of all admissions and platform activity</p>
      <div className="admin-stats">
        <div className="a-stat"><div className="a-stat-label">Total Applications</div><div className="a-stat-value">{apps.length}</div></div>
        <div className="a-stat"><div className="a-stat-label">New Applications</div><div className="a-stat-value">{newApps}</div></div>
        <div className="a-stat"><div className="a-stat-label">Accepted</div><div className="a-stat-value">{accepted}</div></div>
        <div className="a-stat"><div className="a-stat-label">Unread Contact Messages</div><div className="a-stat-value">{unread}</div></div>
        <div className="a-stat"><div className="a-stat-label">Active Courses</div><div className="a-stat-value">{courses.length}</div></div>
        <div className="a-stat"><div className="a-stat-label">Announcements</div><div className="a-stat-value">{announcements.length}</div></div>
        <div className="a-stat"><div className="a-stat-label">Student Messages</div><div className="a-stat-value">{unreadThreads}</div></div>
      </div>
      <div className="admin-grid2">
        <div className="admin-card">
          <div className="admin-card-header"><h3>Recent Applications</h3></div>
          {apps.length === 0 ? <p className="a-empty">No applications on record.</p> : apps.slice(0, 6).map(a => (
            <div className="a-recent-item" key={a.id}>
              <div><div className="a-recent-name">{a.fullName}</div><div className="a-recent-sub">{a.program}</div></div>
              <span className={`badge b-${a.status}`}>{statusLabels[a.status]}</span>
            </div>
          ))}
        </div>
        <div className="admin-card">
          <div className="admin-card-header"><h3>Recent Messages</h3></div>
          {contacts.length === 0 ? <p className="a-empty">No messages on record.</p> : contacts.slice(0, 6).map(c => (
            <div className="a-recent-item" key={c.id}>
              <div><div className="a-recent-name">{c.fullName}</div><div className="a-recent-sub">{c.subject}</div></div>
              <span className={`badge ${c.read ? 'b-read' : 'b-unread'}`}>{c.read ? 'Read' : 'Unread'}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

// ── Applications Tab ──────────────────────────────────────────────────────────
const ApplicationsTab: React.FC<{ apps: Application[] }> = ({ apps }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState<Application | null>(null);

  const filtered = apps.filter(a => {
    const s = search.toLowerCase();
    return (filter === 'all' || a.status === filter) &&
      (a.fullName.toLowerCase().includes(s) || a.email.toLowerCase().includes(s) ||
       a.program.toLowerCase().includes(s) || a.country.toLowerCase().includes(s));
  });

  return (
    <>
      <h2 className="admin-page-title">Applications</h2>
      <p className="admin-page-sub">{apps.length} total application records</p>
      <div className="admin-card">
        <div className="admin-toolbar">
          <input className="a-search" placeholder="Search by name, email, or program..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="a-select" value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="new">New</option>
            <option value="reviewing">Reviewing</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
          <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem', marginLeft: 'auto', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>{filtered.length} records</span>
        </div>
        <div className="a-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Applicant</th><th>Email</th><th>Program</th><th>Degree</th><th>Country</th><th>Status</th><th>Date</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="a-empty">No records found.</td></tr>
              ) : filtered.map(a => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>{a.fullName}</td>
                  <td>{a.email}</td>
                  <td>{a.program}</td>
                  <td>{degreeLabels[a.degree] || a.degree}</td>
                  <td>{a.country}</td>
                  <td><span className={`badge b-${a.status}`}>{statusLabels[a.status]}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmt(a.createdAt)}</td>
                  <td><button className="a-btn a-btn-detail" onClick={() => setSelected(a)}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {selected && <AppModal app={selected} onClose={() => setSelected(null)} />}
    </>
  );
};

// ── Contacts Tab ──────────────────────────────────────────────────────────────
const ContactsTab: React.FC<{ contacts: ContactMessage[] }> = ({ contacts }) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState<ContactMessage | null>(null);

  const filtered = contacts.filter(c => {
    const s = search.toLowerCase();
    return (filter === 'all' || (filter === 'unread' && !c.read) || (filter === 'read' && c.read)) &&
      (c.fullName.toLowerCase().includes(s) || c.email.toLowerCase().includes(s) || c.subject.toLowerCase().includes(s));
  });

  const unread = contacts.filter(c => !c.read).length;

  return (
    <>
      <h2 className="admin-page-title">Contact Messages</h2>
      <p className="admin-page-sub">{contacts.length} total messages &mdash; {unread} unread</p>
      <div className="admin-card">
        <div className="admin-toolbar">
          <input className="a-search" placeholder="Search by name, email, or subject..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="a-select" value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All Messages</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
          <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.78rem', marginLeft: 'auto', fontFamily: "'Segoe UI', system-ui, sans-serif" }}>{filtered.length} records</span>
        </div>
        <div className="a-table-wrap">
          <table>
            <thead>
              <tr><th>Sender</th><th>Email</th><th>Phone</th><th>Subject</th><th>Status</th><th>Date</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={7} className="a-empty">No records found.</td></tr>
              ) : filtered.map(c => (
                <tr key={c.id}>
                  <td style={{ fontWeight: c.read ? 'normal' : 600, color: c.read ? undefined : 'rgba(255,255,255,0.85)' }}>{c.fullName}</td>
                  <td>{c.email}</td>
                  <td>{c.phone || '—'}</td>
                  <td>{c.subject}</td>
                  <td><span className={`badge ${c.read ? 'b-read' : 'b-unread'}`}>{c.read ? 'Read' : 'Unread'}</span></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{fmt(c.createdAt)}</td>
                  <td><button className="a-btn a-btn-detail" onClick={() => setSelected(c)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {selected && <ContactModal msg={selected} onClose={() => setSelected(null)} />}
    </>
  );
};

// ── Main Admin Panel ──────────────────────────────────────────────────────────
const AdminPanel: React.FC = () => {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('uaa_admin') === '1');
  const [tab, setTab] = useState<Tab>('dashboard');
  const [applications, setApplications] = useState<Application[]>([]);
  const [contacts, setContacts] = useState<ContactMessage[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [threads, setThreads] = useState<MessageThread[]>([]);

  useEffect(() => {
    if (!unlocked) return;
    const u1 = onValue(ref(db, 'applications'), snap => {
      const d = snap.val();
      setApplications(d ? Object.entries(d).map(([id, v]) => ({ id, ...(v as Application) })).sort((a, b) => b.createdAt - a.createdAt) : []);
    });
    const u2 = onValue(ref(db, 'contacts'), snap => {
      const d = snap.val();
      setContacts(d ? Object.entries(d).map(([id, v]) => ({ id, ...(v as ContactMessage) })).sort((a, b) => b.createdAt - a.createdAt) : []);
    });
    const u3 = onValue(ref(db, 'courses'), snap => {
      const d = snap.val();
      setCourses(d ? Object.entries(d).map(([id, v]) => ({ id, ...(v as Course) })) : []);
    });
    const u4 = onValue(ref(db, 'announcements'), snap => {
      const d = snap.val();
      setAnnouncements(d ? Object.entries(d).map(([id, v]) => ({ id, ...(v as Announcement) })) : []);
    });
    const u5 = onValue(ref(db, 'messageThreads'), snap => {
      const d = snap.val();
      setThreads(d ? Object.entries(d).map(([uid, v]) => ({ uid, ...(v as Omit<MessageThread, 'uid'>) })) : []);
    });
    return () => { u1(); u2(); u3(); u4(); u5(); };
  }, [unlocked]);

  if (!unlocked) return <LockScreen onUnlock={() => setUnlocked(true)} />;

  const logout = () => { sessionStorage.removeItem('uaa_admin'); setUnlocked(false); };

  const newAppsCount = applications.filter(a => a.status === 'new').length;
  const unreadContactsCount = contacts.filter(c => !c.read).length;
  const unreadThreadsCount = threads.filter(t => t.unreadByAdmin).length;

  const navItems: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'applications', label: newAppsCount ? `Applications (${newAppsCount})` : 'Applications' },
    { id: 'contacts', label: unreadContactsCount ? `Messages (${unreadContactsCount})` : 'Messages' },
    { id: 'students', label: 'Students' },
    { id: 'courses', label: 'Courses' },
    { id: 'exams', label: 'Exams' },
    { id: 'announcements', label: 'Announcements' },
    { id: 'studentMessages', label: unreadThreadsCount ? `Student Messages (${unreadThreadsCount})` : 'Student Messages' },
  ];

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-logo">
          <h2>UAA Administration</h2>
          <span>University of American Atlantic</span>
        </div>
        <nav className="admin-nav">
          {navItems.map(n => (
            <button
              key={n.id}
              className={`admin-nav-item ${tab === n.id ? 'active' : ''}`}
              onClick={() => setTab(n.id)}
            >
              {n.label}
            </button>
          ))}
        </nav>
        <div className="admin-sidebar-foot">
          <button className="admin-logout" onClick={logout}>Sign Out</button>
        </div>
      </aside>

      <main className="admin-main">
        {tab === 'dashboard' && (
          <DashboardTab
            apps={applications}
            contacts={contacts}
            courses={courses}
            announcements={announcements}
            unreadThreads={unreadThreadsCount}
          />
        )}
        {tab === 'applications' && <ApplicationsTab apps={applications} />}
        {tab === 'contacts' && <ContactsTab contacts={contacts} />}
        {tab === 'students' && <StudentsTab courses={courses} />}
        {tab === 'courses' && <CoursesTab />}
        {tab === 'exams' && <ExamsTab courses={courses} />}
        {tab === 'announcements' && <AnnouncementsTab />}
        {tab === 'studentMessages' && <AdminMessagesTab />}
      </main>
    </div>
  );
};

export default AdminPanel;
